import { generatePath } from 'react-router';

export const relativeRoutes = {
  HOME: 'home',
  PROFILE: 'profile',
  REGISTRY: 'registry',
  DECISION_RECORDS: 'decision-records',
  RUNTIME_ENVIRONMENTS: 'environments',
  RUNTIME_ENVIRONMENT: 'environmentId',
  GIT_REPOSITORIES: 'git-repositories',
  RUNTIME_ENVIRONMENTS_DEPLOYMENTS: 'deployments',
  RUNTIME_ENVIRONMENTS_TOKEN: 'token',
  ARTIFACTS: 'artifacts',
  ARTIFACT_VERSIONS: 'versions',
  WORKFLOWS: 'workflows',
  RELEASES: 'releases',
  FORBIDDEN: 'forbidden',
  LOGIN: 'login',
  ACCOUNT: 'account',
  SETTINGS: 'settings',
  PREFERENCES: 'preferences',
  CALLBACK: 'callback',
  TEAM_MANAGEMENT: 'team',
  PAYMENT: 'payment',
  TOKEN_MANAGEMENT: 'token',
  GENERATE: 'generate',
  RENEW: 'silent-renew',
  USER_INFO: 'user-info',
  SIGN_UP: 'sign-up',
  PROVIDERS: 'providers',
  OAUTH_CALLBACK: ':provider/callback',
  USAGE: 'usage',
  SLACK_CONNECT: 'slack-connect',
  PASSWORD_AUTH: 'password-auth',
  LOGIN_DESKTOP: 'login/desktop',
  SIGN_UP_DESKTOP: 'desktop',
  EMAIL_PASSWORD: 'email-password',
  VERIFICATION_CODE: 'verification',
  TEAM_REGISTER: 'team-register',
  CONFIRM_INVITATION: 'confirm-invitation',
  DOWNLOAD: 'download',
} as const;

export const routes = {
  DASHBOARD: {
    // Default authenticated landing; currently aliases REGISTRY.ROOT.
    ROOT: `/${relativeRoutes.REGISTRY}`,
    HOME: `/${relativeRoutes.HOME}`,
    DECISION_RECORDS: `/${relativeRoutes.DECISION_RECORDS}`,
    RUNTIME_ENVIRONMENTS: {
      ROOT: `/${relativeRoutes.RUNTIME_ENVIRONMENTS}`,
      ENVIRONMENT: {
        ROOT: `/${relativeRoutes.RUNTIME_ENVIRONMENTS}/:${relativeRoutes.RUNTIME_ENVIRONMENT}`,
        DEPLOYMENTS: `/${relativeRoutes.RUNTIME_ENVIRONMENTS}/:${relativeRoutes.RUNTIME_ENVIRONMENT}/${relativeRoutes.RUNTIME_ENVIRONMENTS_DEPLOYMENTS}`,
        TOKEN: `/${relativeRoutes.RUNTIME_ENVIRONMENTS}/:${relativeRoutes.RUNTIME_ENVIRONMENT}/${relativeRoutes.RUNTIME_ENVIRONMENTS_TOKEN}`,
      },
    },
    REGISTRY: {
      ROOT: `/${relativeRoutes.REGISTRY}`,
      GIT_REPOSITORIES: `/${relativeRoutes.REGISTRY}/${relativeRoutes.GIT_REPOSITORIES}`,
      ARTIFACTS: `/${relativeRoutes.REGISTRY}/${relativeRoutes.ARTIFACTS}`,
      STANDALONE_ARTIFACT_VERSIONS: `/${relativeRoutes.REGISTRY}/${relativeRoutes.ARTIFACTS}/:packageType/:artifactName/${relativeRoutes.ARTIFACT_VERSIONS}`,
      REPOSITORY: {
        ROOT: `/${relativeRoutes.REGISTRY}/${relativeRoutes.GIT_REPOSITORIES}/:owner/:name`,
        ARTIFACTS: `/${relativeRoutes.REGISTRY}/${relativeRoutes.GIT_REPOSITORIES}/:owner/:name/${relativeRoutes.ARTIFACTS}`,
        WORKFLOWS: `/${relativeRoutes.REGISTRY}/${relativeRoutes.GIT_REPOSITORIES}/:owner/:name/${relativeRoutes.WORKFLOWS}`,
        RELEASES: `/${relativeRoutes.REGISTRY}/${relativeRoutes.GIT_REPOSITORIES}/:owner/:name/${relativeRoutes.RELEASES}`,
        DECISION_RECORDS: `/${relativeRoutes.REGISTRY}/${relativeRoutes.GIT_REPOSITORIES}/:owner/:name/${relativeRoutes.DECISION_RECORDS}`,
        ARTIFACT_VERSIONS: `/${relativeRoutes.REGISTRY}/${relativeRoutes.GIT_REPOSITORIES}/:owner/:name/${relativeRoutes.ARTIFACTS}/:packageType/:artifactName/${relativeRoutes.ARTIFACT_VERSIONS}`,
        RELEASE_ARTIFACTS: `/${relativeRoutes.REGISTRY}/${relativeRoutes.GIT_REPOSITORIES}/:owner/:name/${relativeRoutes.RELEASES}/:workflowName/:runNumber/${relativeRoutes.ARTIFACTS}`,
      },
    },
    FORBIDDEN: `/${relativeRoutes.FORBIDDEN}`,
    ACCOUNT: {
      ROOT: `/${relativeRoutes.ACCOUNT}`,
      PROFILE: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.PROFILE}`,
      PAYMENT: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.PAYMENT}`,
      SETTINGS: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.SETTINGS}`,
      PREFERENCES: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.PREFERENCES}`,
      TEAM_MANAGEMENT: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.TEAM_MANAGEMENT}`,
      TOKEN_MANAGEMENT: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.TOKEN_MANAGEMENT}`,
      TOKEN_GENERATE: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.TOKEN_MANAGEMENT}/${relativeRoutes.GENERATE}`,
      USAGE: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.USAGE}`,
      SLACK_CONNECT: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.SLACK_CONNECT}`,
      PASSWORD_AUTH: `/${relativeRoutes.ACCOUNT}/${relativeRoutes.PASSWORD_AUTH}`,
    },
  },
  AUTH: {
    ROOT: '/auth',
    LOGIN: `/auth/${relativeRoutes.LOGIN}`,
    CALLBACK: `/auth/${relativeRoutes.CALLBACK}`,
    RENEW: `/auth/${relativeRoutes.RENEW}`,
  },
  SIGN_UP: {
    ROOT: '/sign-up',
    DESKTOP: `/sign-up/${relativeRoutes.SIGN_UP_DESKTOP}`,
    PROVIDERS: `/sign-up/${relativeRoutes.PROVIDERS}`,
    USER_INFO: `/sign-up/${relativeRoutes.USER_INFO}`,
    EMAIL_PASSWORD: `/sign-up/${relativeRoutes.EMAIL_PASSWORD}`,
    VERIFICATION_CODE: `/sign-up/${relativeRoutes.VERIFICATION_CODE}`,
    GITHUB_OAUTH_CALLBACK: generatePath(
      `/sign-up/${relativeRoutes.OAUTH_CALLBACK}`,
      { provider: 'github' }
    ),
    GOOGLE_OAUTH_CALLBACK: generatePath(
      `/sign-up/${relativeRoutes.OAUTH_CALLBACK}`,
      { provider: 'google' }
    ),
    TEAM_REGISTER: `/sign-up/${relativeRoutes.TEAM_REGISTER}`,
    CONFIRM_INVITATION: `/sign-up/${relativeRoutes.CONFIRM_INVITATION}`,
  },
  DOWNLOAD: {
    ROOT: `/${relativeRoutes.DOWNLOAD}`,
  },
  HOME: {
    ROOT: `/${relativeRoutes.HOME}`,
  },
  GITHUB_CALLBACK: {
    ROOT: '/github/callback',
    PRE_CONFIGURE: '/github/configure-redirect',
  },
} as const;

/** Prefixes for authenticated app areas (used for 404 back-link detection). */
export const APP_ROUTE_PREFIXES = [
  routes.DASHBOARD.REGISTRY.ROOT,
  routes.DASHBOARD.RUNTIME_ENVIRONMENTS.ROOT,
  routes.DASHBOARD.DECISION_RECORDS,
  routes.DASHBOARD.ACCOUNT.ROOT,
  routes.DASHBOARD.FORBIDDEN,
] as const;

export const isAppRoutePath = (pathname: string): boolean =>
  APP_ROUTE_PREFIXES.some((prefix) => pathname.startsWith(prefix));
